import { Zap, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { devLoginEnabled, devLoginRequiresAdmin } from '../lib/devLogin';

/**
 * SignIn — Public landing page for unauthenticated users.
 *
 * Primary action: navigate the user to the Blast app to sign in there.
 * After they sign in, they re-enter the CRM via a Blast deep link with
 * `?token=…`, which authStore.restoreSession consumes.
 *
 * If a recent handoff failed (token rejected by /api/auth/me), the
 * auth store's `error` is populated and we show it as a banner so the
 * user understands why they're back here.
 *
 * In dev / staging builds (VITE_ENABLE_DEV_LOGIN=true), this page also
 * surfaces a small secondary link to /dev/login for role-switching tests.
 */
export const SignIn = () => {
  const blastAppUrl = import.meta.env.VITE_BLAST_APP_URL || 'https://dev.blastathletics.com';
  // Open mode only (local dev server). In deployed builds dev login is an
  // admin-only in-app tool, so reps see only "Sign in via Blast".
  const showDevLogin = devLoginEnabled && !devLoginRequiresAdmin;
  const error = useAuthStore((s) => s.error);

  const handleSignIn = () => {
    window.location.href = blastAppUrl;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="w-16 h-16 bg-blast-100 dark:bg-blast-950 rounded-2xl flex items-center justify-center mx-auto">
            <Zap className="w-10 h-10 text-blast-600" />
          </div>
          <h1 className="mt-4 text-3xl font-bold text-gray-900 dark:text-gray-100">
            Blast CRM
          </h1>
          <p className="mt-3 text-base text-gray-600 dark:text-gray-300">
            Please sign in via your Blast account to continue.
          </p>
        </div>

        {/* Auth-handoff failure banner */}
        {error && (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950 p-4 text-sm text-amber-900 dark:text-amber-100"
          >
            <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
            <span>{error}</span>
          </div>
        )}

        {/* Primary action */}
        <div className="card p-6">
          <button
            onClick={handleSignIn}
            className="w-full px-4 py-3 bg-blast-600 text-white rounded-lg font-medium hover:bg-blast-700 transition-colors"
          >
            Sign in via Blast
          </button>
        </div>

        {/* Dev-only secondary link */}
        {showDevLogin && (
          <div className="text-center">
            <Link
              to="/dev/login"
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-blast-600 dark:hover:text-blast-400 underline"
            >
              Use developer login →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
